import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	ListItemText,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	alpha,
} from "@mui/material";
import {
	Assessment,
	CheckCircle,
	Close,
	Error as ErrorIcon,
	Person,
	PlayArrow,
	Refresh,
	Repeat,
	Replay,
	Schedule,
	Speed,
	Stop,
	Terminal,
	OpenInNew,
	TrendingUp,
	ViewList,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import executionService from "../services/executionService";
import socketService from "../services/socketService";
import ExecutionDetailPanel from "../components/ExecutionDetailPanel";
import { HistoryDots, STABILITY_TAGS, StabilityChip } from "../components/Stability";
import ReportExportMenu from "../components/ReportExportMenu";
import {
	fetchSuiteExecutions,
	fetchSuiteExecutionDetail,
	fetchChildExecutions,
	cancelSuiteExecution,
	fetchSuiteExecutionReport,
	fetchSuiteReport,
	rerunFailedTests,
	selectSuiteExecution,
	clearSuiteReport,
} from "../store/slices/suiteExecutionSlice";
import { formatDateTime, formatDateTimeShort } from "../utils/dateUtils";

const getStatusChip = (status) => {
	switch (status) {
		case "SUCCESS":
		case "COMPLETED":
			return { color: "success", icon: <CheckCircle /> };
		case "FAILED":
			return { color: "error", icon: <ErrorIcon /> };
		case "RUNNING":
			return { color: "info", icon: <PlayArrow /> };
		case "QUEUED":
		case "PENDING":
			return { color: "warning", icon: <Schedule /> };
		case "CANCELLED":
			return { color: "default", icon: <Stop /> };
		default:
			return { color: "default", icon: <Schedule /> };
	}
};

const RUNNING_STATUSES = ["PENDING", "RUNNING", "QUEUED"];

// A "Run in Loop" of one test (no suite): its runs are the test's iterations
const LoopChip = ({ iterations, sequential }) => (
	<Chip
		icon={<Repeat />}
		label={`Loop ×${iterations}${
			sequential === undefined ? "" : sequential ? " · sequential" : " · parallel"
		}`}
		size="small"
		color="secondary"
		variant="outlined"
		sx={{ height: 20, fontSize: "0.7rem", flexShrink: 0 }}
	/>
);

// A run started from a CI pipeline with an API token (backend CiService)
const CI_PROVIDER_LABELS = { github: "GitHub Actions", gitlab: "GitLab CI", jenkins: "Jenkins", azure: "Azure DevOps" };
const CiChip = ({ ci, name }) => {
	// A run of chosen tests is named after its label already
	const label = ci?.label && ci.label !== name ? ci.label : null;
	const details = [
		CI_PROVIDER_LABELS[ci?.provider],
		ci?.branch && `branch ${ci.branch}`,
		ci?.commit && `commit ${ci.commit.slice(0, 8)}`,
		ci?.tokenName && `token “${ci.tokenName}”`,
	].filter(Boolean);
	return (
		<Tooltip title={details.length ? `Started from CI: ${details.join(" · ")}` : "Started from CI"}>
			<Chip
				icon={<Terminal />}
				label={label ? `CI · ${label}` : "CI"}
				size="small"
				color="info"
				variant="outlined"
				sx={{ height: 20, fontSize: "0.7rem", flexShrink: 0, maxWidth: 180 }}
			/>
		</Tooltip>
	);
};

// Only web links: the build URL comes from the pipeline
const safeBuildUrl = (url) => (url && /^https?:\/\//i.test(url) ? url : null);

const formatDuration = (ms) => {
	if (!ms) return "-";
	if (ms < 1000) return `${ms}ms`;
	const seconds = Math.floor(ms / 1000);
	if (seconds < 60) return `${seconds}s`;
	const minutes = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${minutes}m ${secs}s`;
};

export default function SuiteExecutions() {
	const dispatch = useDispatch();
	const { enqueueSnackbar } = useSnackbar();
	const location = useLocation();
	const loopRefreshTimers = useRef([]);

	const {
		suiteExecutions,
		selectedSuiteExecution,
		childExecutions,
		executionReport,
		suiteReport,
		loading,
		childrenLoading,
		rerunLoading,
		reportLoading,
		suiteReportLoading,
	} = useSelector((state) => state.suiteExecutions);

	const [searchQuery, setSearchQuery] = useState("");
	const [suiteReportOpen, setSuiteReportOpen] = useState(false);
	const [stability, setStability] = useState(null); // {suiteExecutionId, summary, tests} of the selected run
	const [selectedChildExecution, setSelectedChildExecution] = useState(null);
	const [childDetailLoading, setChildDetailLoading] = useState(false);
	const [liveStatus, setLiveStatus] = useState({});
	const subscribedIdsRef = useRef(new Set());
	const hasRunningRef = useRef(false);
	// Read by the poll interval, which is only recreated when the selection changes
	const selectedSuiteExecutionStatusRef = useRef(null);
	const [searchParams] = useSearchParams();
	const focusedSuiteExecutionId = searchParams.get("suiteExecutionId");
	const [stoppingSuite, setStoppingSuite] = useState(false);

	// Arriving from "Run" on the Suites page: select the run that was just started
	useEffect(() => {
		if (focusedSuiteExecutionId) {
			dispatch(fetchSuiteExecutionDetail(focusedSuiteExecutionId));
			setSelectedChildExecution(null);
		}
	}, [dispatch, focusedSuiteExecutionId]);

	// Track whether any suite execution is running
	useEffect(() => {
		const hasRunning = suiteExecutions.some((e) =>
			RUNNING_STATUSES.includes(e.status),
		);
		const selectedIsRunning =
			selectedSuiteExecution &&
			RUNNING_STATUSES.includes(selectedSuiteExecution.status);
		hasRunningRef.current = hasRunning || selectedIsRunning;
		selectedSuiteExecutionStatusRef.current = selectedSuiteExecution?.status ?? null;
	}, [suiteExecutions, selectedSuiteExecution]);

	// Initial load
	useEffect(() => {
		dispatch(fetchSuiteExecutions());

		// When navigated here from a loop run, suite executions are created
		// asynchronously. Retry loading so they appear without manual refresh.
		if (location.state?.loopStarted) {
			const delays = [1000, 2500, 4500];
			loopRefreshTimers.current = delays.map((d) =>
				setTimeout(() => dispatch(fetchSuiteExecutions()), d),
			);
		}

		return () => {
			loopRefreshTimers.current.forEach(clearTimeout);
			loopRefreshTimers.current = [];
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	// Stability of the selected run's tests: refetched as its tests finish (the report's counts change)
	const reportRunId = executionReport?.suiteExecutionId;
	const reportFinished = executionReport ? (executionReport.passedTests || 0) + (executionReport.failedTests?.length || 0) : 0;
	useEffect(() => {
		if (!reportRunId) {
			setStability(null);
			return;
		}
		let cancelled = false;
		executionService
			.getSuiteRunStability(reportRunId)
			.then((data) => !cancelled && setStability(data))
			.catch(() => !cancelled && setStability(null));
		return () => {
			cancelled = true;
		};
	}, [reportRunId, reportFinished]);
	const stabilityFor = (executionId) =>
		stability?.suiteExecutionId === selectedSuiteExecution?.id ? stability.tests.find((t) => t.executionId === executionId) : null;

	// Poll when any suite execution is running — stable interval, no deps on suiteExecutions
	useEffect(() => {
		const pollInterval = setInterval(() => {
			if (!hasRunningRef.current) return;
			dispatch(fetchSuiteExecutions());
			if (selectedSuiteExecution?.id) {
				dispatch(fetchChildExecutions(selectedSuiteExecution.id));
				// Keep the report's live numbers (steps passed so far, …) current
				if (RUNNING_STATUSES.includes(selectedSuiteExecutionStatusRef.current)) {
					dispatch(fetchSuiteExecutionReport(selectedSuiteExecution.id));
				}
			}
		}, 3000);

		return () => clearInterval(pollInterval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, selectedSuiteExecution?.id]);

	// Subscribe to child execution socket events for live step updates
	useEffect(() => {
		if (!childExecutions || childExecutions.length === 0) return;

		const runningChildren = childExecutions.filter((e) =>
			RUNNING_STATUSES.includes(e.status),
		);
		if (runningChildren.length === 0) return;

		const socket = socketService.connect();
		const newIds = new Set();

		runningChildren.forEach((child) => {
			const eventName = `execution:${child.id}`;
			newIds.add(child.id);

			if (!subscribedIdsRef.current.has(child.id)) {
				socketService.watchExecution(child.id);
				socket.on(eventName, (data) => {
					setLiveStatus((prev) => ({ ...prev, [child.id]: data }));

					// Update step results in real-time for the selected child
					if (
						data.event === "step_completed" ||
						data.event === "step_failed" ||
						data.event === "step_skipped"
					) {
						setSelectedChildExecution((prev) => {
							if (!prev || prev.id !== child.id) return prev;
							const stepIndex = data.data?.stepIndex;
							if (stepIndex === undefined) return prev;

							const updatedStepResults = [
								...(prev.stepResults || []),
							];
							const existingIndex = updatedStepResults.findIndex(
								(r) =>
									r.stepIndex === stepIndex ||
									r.metadata?.stepIndex === stepIndex ||
									parseInt(r.stepId, 10) === stepIndex,
							);

							const newStepResult = {
								stepId: String(stepIndex),
								stepIndex: stepIndex,
								type: data.data?.step?.type,
								selector: data.data?.step?.selector,
								status:
									data.event === "step_completed"
										? "SUCCESS"
										: data.event === "step_skipped"
											? "SKIPPED"
											: "FAILED",
								timestamp:
									data.timestamp || new Date().toISOString(),
								errorMessage: data.data?.error || null,
								screenshot: data.data?.screenshot || null,
								metadata: {
									stepIndex: stepIndex,
									value: data.data?.step?.value || null,
									url: data.data?.step?.url || null,
									recovery: data.data?.recovery || null,
									screenshot: data.data?.screenshot || null,
								},
							};

							if (existingIndex >= 0) {
								updatedStepResults[existingIndex] =
									newStepResult;
							} else {
								updatedStepResults.push(newStepResult);
							}

							return { ...prev, stepResults: updatedStepResults };
						});
					}

					// When child execution completes, refresh data
					const finalStatus = {
						completed: "SUCCESS",
						success: "SUCCESS",
						failed: "FAILED",
						cancelled: "CANCELLED",
					}[data.event];
					if (finalStatus) {
						setSelectedChildExecution((prev) =>
							prev && prev.id === child.id
								? {
										...prev,
										status: finalStatus,
										completedAt:
											data.timestamp ||
											new Date().toISOString(),
									}
								: prev,
						);
						// Refresh suite and children after a short delay
						setTimeout(() => {
							dispatch(fetchSuiteExecutions());
							if (selectedSuiteExecution?.id) {
								dispatch(
									fetchChildExecutions(
										selectedSuiteExecution.id,
									),
								);
							}
						}, 1000);
					}
				});
				subscribedIdsRef.current.add(child.id);
			}
		});

		// Cleanup removed subscriptions
		return () => {
			subscribedIdsRef.current.forEach((id) => {
				if (!newIds.has(id)) {
					socket.off(`execution:${id}`);
					socketService.unwatchExecution(id);
					subscribedIdsRef.current.delete(id);
				}
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [childExecutions, dispatch, selectedSuiteExecution?.id]);

	// Cleanup socket on unmount
	useEffect(() => {
		return () => {
			const socket = socketService.connect();
			subscribedIdsRef.current.forEach((id) => {
				socket.off(`execution:${id}`);
				socketService.unwatchExecution(id);
			});
			subscribedIdsRef.current.clear();
			setLiveStatus({});
		};
	}, []);

	useEffect(() => {
		if (selectedSuiteExecution?.id) {
			dispatch(fetchChildExecutions(selectedSuiteExecution.id));
		}
	}, [dispatch, selectedSuiteExecution?.id]);

	// The report is rebuilt from the backend whenever the selected run makes
	// progress (a test finishes) or ends — the polls above refresh the run
	// itself, so this follows without a page refresh.
	useEffect(() => {
		if (selectedSuiteExecution?.id) {
			dispatch(fetchSuiteExecutionReport(selectedSuiteExecution.id));
		}
	}, [
		dispatch,
		selectedSuiteExecution?.id,
		selectedSuiteExecution?.status,
		selectedSuiteExecution?.completedTests,
	]);

	const handleSelectExecution = (exec) => {
		dispatch(selectSuiteExecution(exec));
		setSelectedChildExecution(null);
	};

	const handleSelectChildExecution = useCallback(
		async (exec) => {
			try {
				setChildDetailLoading(true);
				const fullExecution = await executionService.getExecution(
					exec.id,
				);
				setSelectedChildExecution(fullExecution);
			} catch (error) {
				console.error("Error loading execution details:", error);
				enqueueSnackbar("Failed to load execution details", {
					variant: "error",
				});
			} finally {
				setChildDetailLoading(false);
			}
		},
		[enqueueSnackbar],
	);

	const handleOpenSuiteReport = (suiteId) => {
		dispatch(fetchSuiteReport(suiteId));
		setSuiteReportOpen(true);
	};

	const handleCloseSuiteReport = () => {
		setSuiteReportOpen(false);
		dispatch(clearSuiteReport());
	};

	const handleRefresh = () => {
		dispatch(fetchSuiteExecutions());
		if (selectedSuiteExecution?.id) {
			dispatch(fetchChildExecutions(selectedSuiteExecution.id));
			dispatch(fetchSuiteExecutionReport(selectedSuiteExecution.id));
		}
	};

	const handleStopSuite = () => {
		if (!sel?.id) return;
		setStoppingSuite(true);
		dispatch(cancelSuiteExecution(sel.id))
			.unwrap()
			.then(() => {
				enqueueSnackbar("Suite run stopped", { variant: "info" });
			})
			.catch((err) => {
				enqueueSnackbar(
					err?.status === 409
						? "Suite run had already finished"
						: err?.status === 403
							? "You can only stop your own suite runs"
							: "Failed to stop suite run",
					{ variant: err?.status === 409 ? "info" : "error" },
				);
			})
			.finally(() => {
				setStoppingSuite(false);
				dispatch(fetchSuiteExecutions());
				dispatch(fetchChildExecutions(sel.id));
				dispatch(fetchSuiteExecutionReport(sel.id));
			});
	};

	const handleRerunFailed = () => {
		if (!sel?.id) return;
		dispatch(rerunFailedTests(sel.id))
			.unwrap()
			.then(() => {
				enqueueSnackbar("Rerun started for failed tests", {
					variant: "success",
				});
			})
			.catch((err) => {
				enqueueSnackbar(err?.message || "Failed to start rerun", {
					variant: "error",
				});
			});
	};

	const filteredExecutions = suiteExecutions.filter((e) => {
		if (!searchQuery.trim()) return true;
		const q = searchQuery.toLowerCase();
		return (
			e.suiteName?.toLowerCase().includes(q) ||
			e.status?.toLowerCase().includes(q) ||
			e.userId?.toLowerCase().includes(q)
		);
	});

	const sel = selectedSuiteExecution;

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 4,
				}}
			>
				<Box>
					<Typography variant="h4" fontWeight={700}>
						Suite Runs
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mt: 0.5 }}
					>
						View test suite and Run in Loop history and results
					</Typography>
				</Box>
				<IconButton onClick={handleRefresh}>
					<Refresh />
				</IconButton>
			</Box>

			{loading && suiteExecutions.length === 0 ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress />
				</Box>
			) : suiteExecutions.length === 0 ? (
				<Card
					sx={{
						textAlign: "center",
						py: 8,
						background: (theme) =>
							`linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
					}}
				>
					<CardContent>
						<ViewList
							sx={{
								fontSize: 48,
								color: "text.secondary",
								mb: 2,
							}}
						/>
						<Typography variant="h6" gutterBottom>
							No Suite Runs Yet
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Execute a test suite or run a test in a loop to see results here
						</Typography>
					</CardContent>
				</Card>
			) : (
				<Grid container spacing={3}>
					{/* Left panel — suite execution list */}
					<Grid item xs={12} md={4}>
						<Card>
							<CardContent>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										mb: 2,
									}}
								>
									<Typography variant="h6">
										Suite Runs
									</Typography>
									<Chip
										label={`${filteredExecutions.length} total`}
										size="small"
										color="primary"
										variant="outlined"
									/>
								</Box>
								<TextField
									fullWidth
									size="small"
									placeholder="Search by suite name, status..."
									value={searchQuery}
									onChange={(e) =>
										setSearchQuery(e.target.value)
									}
									sx={{ mb: 2 }}
								/>
								<List
									sx={{
										maxHeight: "calc(100vh - 350px)",
										overflow: "auto",
									}}
								>
									{filteredExecutions.length === 0 ? (
										<Box
											sx={{
												textAlign: "center",
												py: 4,
											}}
										>
											<Typography
												variant="body2"
												color="text.secondary"
											>
												No suite runs found
											</Typography>
										</Box>
									) : (
										filteredExecutions.map((exec) => {
											const chip = getStatusChip(
												exec.status,
											);
											return (
												<ListItem
													key={exec.id}
													button
													selected={
														sel?.id === exec.id
													}
													onClick={() =>
														handleSelectExecution(
															exec,
														)
													}
													sx={{
														borderRadius: 1,
														mb: 1,
														border: 1,
														borderColor:
															sel?.id === exec.id
																? "primary.main"
																: "divider",
														bgcolor:
															sel?.id === exec.id
																? (theme) =>
																		alpha(
																			theme
																				.palette
																				.primary
																				.main,
																			0.08,
																		)
																: "transparent",
														"&:hover": {
															borderColor:
																"primary.light",
														},
													}}
												>
													<ListItemText
														primary={
															<Stack
																spacing={0.5}
															>
																<Box
																	sx={{
																		display:
																			"flex",
																		alignItems:
																			"center",
																		justifyContent:
																			"space-between",
																	}}
																>
																	<Chip
																		label={
																			exec.status
																		}
																		size="small"
																		color={
																			chip.color
																		}
																		icon={
																			chip.icon
																		}
																	/>
																	<Typography
																		variant="caption"
																		color="text.secondary"
																	>
																		{exec.startedAt
																			? formatDateTimeShort(exec.startedAt)
																			: "N/A"}
																	</Typography>
																</Box>
																<Box
																	sx={{
																		display:
																			"flex",
																		alignItems:
																			"center",
																		gap: 1,
																		minWidth: 0,
																	}}
																>
																	{exec.iterations && (
																		<LoopChip
																			iterations={
																				exec.iterations
																			}
																		/>
																	)}
																	{exec.trigger === "CI" && (
																		<CiChip ci={exec.ci} name={exec.suiteName} />
																	)}
																	<Typography
																		variant="body2"
																		fontWeight={
																			600
																		}
																		noWrap
																	>
																		{exec.suiteName ||
																			"Unnamed Suite"}
																	</Typography>
																</Box>
																<Box
																	sx={{
																		display:
																			"flex",
																		alignItems:
																			"center",
																		gap: 1,
																	}}
																>
																	<Chip
																		label={`${exec.passCount || 0} passed`}
																		size="small"
																		color="success"
																		variant="outlined"
																		sx={{
																			height: 20,
																			fontSize:
																				"0.7rem",
																		}}
																	/>
																	{exec.failCount >
																		0 && (
																		<Chip
																			label={`${exec.failCount} failed`}
																			size="small"
																			color="error"
																			variant="outlined"
																			sx={{
																				height: 20,
																				fontSize:
																					"0.7rem",
																			}}
																		/>
																	)}
																</Box>
																<Box
																	sx={{
																		display:
																			"flex",
																		alignItems:
																			"center",
																		gap: 0.5,
																	}}
																>
																	<Person
																		sx={{
																			fontSize:
																				"0.875rem",
																			color: "text.secondary",
																		}}
																	/>
																	<Typography
																		variant="caption"
																		color="text.secondary"
																	>
																		{exec.userId?.substring(
																			0,
																			12,
																		) ||
																			"Unknown"}
																	</Typography>
																	{exec.duration && (
																		<Typography
																			variant="caption"
																			color="text.secondary"
																			sx={{
																				ml: "auto",
																			}}
																		>
																			{formatDuration(
																				exec.duration,
																			)}
																		</Typography>
																	)}
																</Box>
															</Stack>
														}
													/>
												</ListItem>
											);
										})
									)}
								</List>
							</CardContent>
						</Card>
					</Grid>

					{/* Right panel — detail view */}
					<Grid item xs={12} md={8}>
						{!sel ? (
							<Card sx={{ textAlign: "center", py: 8 }}>
								<CardContent>
									<Typography
										variant="body1"
										color="text.secondary"
									>
										Select a suite run to view details
									</Typography>
								</CardContent>
							</Card>
						) : (
							<Card>
								<CardContent>
									{/* Header */}
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "flex-start",
											mb: 3,
										}}
									>
										<Box>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
												}}
											>
												<Typography
													variant="h5"
													fontWeight={700}
												>
													{sel.suiteName ||
														"Unnamed Suite"}
												</Typography>
												{sel.iterations && (
													<LoopChip
														iterations={
															sel.iterations
														}
														sequential={
															sel.maxParallel ===
															1
														}
													/>
												)}
												{sel.trigger === "CI" && (
													<CiChip ci={sel.ci} name={sel.suiteName} />
												)}
												{sel.trigger === "CI" &&
													safeBuildUrl(sel.ci?.buildUrl) && (
														<Button
															size="small"
															endIcon={<OpenInNew fontSize="small" />}
															href={safeBuildUrl(sel.ci.buildUrl)}
															target="_blank"
															rel="noopener noreferrer"
															sx={{ py: 0 }}
														>
															View build
														</Button>
													)}
											</Box>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ mt: 0.5 }}
											>
												Started:{" "}
												{sel.startedAt
													? formatDateTime(sel.startedAt)
													: "N/A"}
												{sel.completedAt &&
													` | Completed: ${formatDateTime(sel.completedAt)}`}
												{sel.duration &&
													` | Duration: ${formatDuration(sel.duration)}`}
											</Typography>
										</Box>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
										>
											{sel.suiteId && (
												<Button
													variant="outlined"
													size="small"
													startIcon={<Assessment />}
													onClick={() =>
														handleOpenSuiteReport(
															sel.suiteId,
														)
													}
												>
													Suite Report
												</Button>
											)}
											<ReportExportMenu run={sel} />
											{RUNNING_STATUSES.includes(
												sel.status,
											) && sel.canStop !== false && (
												<Button
													variant="outlined"
													color="error"
													size="small"
													startIcon={
														stoppingSuite ? (
															<CircularProgress
																size={16}
																color="inherit"
															/>
														) : (
															<Stop />
														)
													}
													disabled={stoppingSuite}
													onClick={handleStopSuite}
												>
													{stoppingSuite
														? "Stopping…"
														: "Stop"}
												</Button>
											)}
											{sel.failCount > 0 &&
												sel.status !== "RUNNING" && (
													<Button
														variant="outlined"
														color="error"
														size="small"
														startIcon={
															rerunLoading ? (
																<CircularProgress
																	size={16}
																	color="inherit"
																/>
															) : (
																<Replay />
															)
														}
														disabled={rerunLoading}
														onClick={
															handleRerunFailed
														}
													>
														Rerun Failed
													</Button>
												)}
											{(() => {
												const chip = getStatusChip(
													sel.status,
												);
												return (
													<Chip
														label={sel.status}
														color={chip.color}
														icon={chip.icon}
													/>
												);
											})()}
										</Stack>
									</Box>

									{/* Stats bar */}
									<Box
										sx={{
											display: "flex",
											gap: 2,
											mb: 3,
											flexWrap: "wrap",
										}}
									>
										<Chip
											label={`Total: ${sel.testCount || 0}`}
											variant="outlined"
										/>
										<Chip
											label={`Passed: ${sel.passCount || 0}`}
											color="success"
											variant="outlined"
										/>
										<Chip
											label={`Failed: ${sel.failCount || 0}`}
											color="error"
											variant="outlined"
										/>
										{sel.skipCount > 0 && (
											<Chip
												label={`Skipped: ${sel.skipCount}`}
												variant="outlined"
											/>
										)}
									</Box>

									{sel.errorMessage && (
										<Box
											sx={{
												bgcolor: (theme) =>
													alpha(
														theme.palette.error
															.main,
														0.08,
													),
												p: 2,
												borderRadius: 1,
												mb: 3,
											}}
										>
											<Typography
												variant="body2"
												color="error"
											>
												{sel.errorMessage}
											</Typography>
										</Box>
									)}

									{/* Child test executions table */}
									<Typography variant="h6" sx={{ mb: 2 }}>
										Test Executions
									</Typography>

									{childrenLoading &&
									childExecutions.length === 0 ? (
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												py: 4,
											}}
										>
											<CircularProgress size={24} />
										</Box>
									) : childExecutions.length === 0 ? (
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ py: 2 }}
										>
											No test executions found for this
											suite run
										</Typography>
									) : (
										<>
											<TableContainer>
												<Table size="small">
													<TableHead>
														<TableRow>
															<TableCell>
																Test Name
															</TableCell>
															<TableCell>
																Status
															</TableCell>
															<TableCell>
																Duration
															</TableCell>
															<TableCell>
																Steps
															</TableCell>
															<TableCell>
																Error
															</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{childExecutions.map(
															(exec) => {
																const chip =
																	getStatusChip(
																		exec.status,
																	);
																const isSelected =
																	selectedChildExecution?.id ===
																	exec.id;
																return (
																	<TableRow
																		key={
																			exec.id
																		}
																		hover
																		onClick={() =>
																			handleSelectChildExecution(
																				exec,
																			)
																		}
																		sx={{
																			cursor: "pointer",
																			bgcolor:
																				isSelected
																					? (
																							theme,
																						) =>
																							alpha(
																								theme
																									.palette
																									.primary
																									.main,
																								0.08,
																							)
																					: "transparent",
																			borderLeft:
																				isSelected
																					? "3px solid"
																					: "3px solid transparent",
																			borderLeftColor:
																				isSelected
																					? "primary.main"
																					: "transparent",
																		}}
																	>
																		<TableCell>
																			<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
																				<Typography variant="body2" fontWeight={600}>
																					{exec.testName || "Unnamed Test"}
																				</Typography>
																				<StabilityChip tag={stabilityFor(exec.id)?.tag} />
																				<HistoryDots history={stabilityFor(exec.id)?.history} />
																			</Box>
																			{exec.dataSetName && (
																				<Typography
																					variant="caption"
																					color="text.secondary"
																				>
																					Dataset:{" "}
																					{
																						exec.dataSetName
																					}
																					{exec.dataRowIndex !=
																						null &&
																						` (Row ${exec.dataRowIndex + 1})`}
																				</Typography>
																			)}
																		</TableCell>
																		<TableCell>
																			<Chip
																				label={
																					exec.status
																				}
																				size="small"
																				color={
																					chip.color
																				}
																				icon={
																					chip.icon
																				}
																			/>
																		</TableCell>
																		<TableCell>
																			{formatDuration(
																				exec.duration,
																			)}
																		</TableCell>
																		<TableCell>
																			{exec
																				.stats
																				?.successfulSteps ||
																				0}
																			/
																			{exec
																				.stats
																				?.totalSteps ||
																				0}
																			{exec.stats
																				?.skippedSteps >
																				0 &&
																				` (${exec.stats.skippedSteps} skipped)`}
																		</TableCell>
																		<TableCell
																			sx={{
																				maxWidth: 200,
																			}}
																		>
																			{exec.errorMessage ? (
																				<Tooltip
																					title={
																						exec.errorMessage
																					}
																				>
																					<Typography
																						variant="caption"
																						color="error"
																						noWrap
																					>
																						{
																							exec.errorMessage
																						}
																					</Typography>
																				</Tooltip>
																			) : (
																				"-"
																			)}
																		</TableCell>
																	</TableRow>
																);
															},
														)}
													</TableBody>
												</Table>
											</TableContainer>

											{/* Selected child execution detail */}
											{childDetailLoading && (
												<Box
													sx={{
														display: "flex",
														justifyContent:
															"center",
														py: 4,
													}}
												>
													<CircularProgress
														size={24}
													/>
												</Box>
											)}
											{selectedChildExecution &&
												!childDetailLoading && (
													<Box sx={{ mt: 3 }}>
														<Divider
															sx={{ mb: 3 }}
														/>
														<ExecutionDetailPanel
															execution={
																selectedChildExecution
															}
															liveStatus={
																liveStatus[
																	selectedChildExecution
																		?.id
																]
															}
															disableAnimation
														/>
													</Box>
												)}
										</>
									)}

									{/* Execution Report Section */}
									{executionReport && !reportLoading && (
										<>
											<Divider sx={{ my: 3 }} />
											<Typography
												variant="h6"
												sx={{ mb: 2 }}
											>
												<Assessment
													sx={{
														verticalAlign: "middle",
														mr: 1,
														fontSize: "1.3rem",
													}}
												/>
												Execution Report
											</Typography>

											{/* Pass Rate Bar */}
											<Box sx={{ mb: 3 }}>
												<Box
													sx={{
														display: "flex",
														justifyContent:
															"space-between",
														mb: 0.5,
													}}
												>
													<Typography
														variant="body2"
														fontWeight={600}
													>
														Pass Rate
													</Typography>
													<Typography
														variant="body2"
														fontWeight={700}
														color={
															executionReport.passRate >=
															80
																? "success.main"
																: executionReport.passRate >=
																	  50
																	? "warning.main"
																	: "error.main"
														}
													>
														{
															executionReport.passRate
														}
														%
													</Typography>
												</Box>
												<LinearProgress
													variant="determinate"
													value={
														executionReport.passRate
													}
													color={
														executionReport.passRate >=
														80
															? "success"
															: executionReport.passRate >=
																  50
																? "warning"
																: "error"
													}
													sx={{
														height: 10,
														borderRadius: 5,
													}}
												/>
											</Box>

											{/* Stability: how this run's results compare with earlier runs */}
											{stability?.suiteExecutionId === selectedSuiteExecution?.id && stability.tests.length > 0 && (
												<Box sx={{ mb: 3, p: 2, borderRadius: 1, border: 1, borderColor: "divider" }}>
													<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
														Compared with earlier runs
													</Typography>
													<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
														Each test against its last {stability.window} runs
														{stability.comparedWith === "suite" ? " in this suite" : ""}. Hover a tag for what it means.
													</Typography>
													{(() => {
														const s = stability.summary;
														const items = [
															["NEW_FAILURE", s.newFailures],
															["ALWAYS_FAILING", s.alwaysFailing],
															["FLAKY", s.flaky],
															["FIXED", s.fixed],
															["SLOW", s.slow],
														].filter(([, n]) => n > 0);
														const stillOther = s.stillFailing - s.alwaysFailing;
														return items.length === 0 && stillOther <= 0 ? (
															<Typography variant="body2" color="text.secondary">
																No changes: every test did what it did last time.
															</Typography>
														) : (
															<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
																{items.map(([tag, n]) => (
																	<Tooltip key={tag} title={STABILITY_TAGS[tag].help}>
																		<Chip
																			size="small"
																			color={STABILITY_TAGS[tag].color}
																			variant={STABILITY_TAGS[tag].variant}
																			label={`${n} ${STABILITY_TAGS[tag].label.toLowerCase()}`}
																			sx={{ fontWeight: 600 }}
																		/>
																	</Tooltip>
																))}
																{stillOther > 0 && (
																	<Typography variant="body2" color="text.secondary">
																		{stillOther} still failing since the last run
																	</Typography>
																)}
															</Stack>
														);
													})()}
												</Box>
											)}
											{/* Duration & Step Stats */}
											<Grid
												container
												spacing={2}
												sx={{ mb: 3 }}
											>
												<Grid item xs={4}>
													<Box
														sx={{
															p: 2,
															borderRadius: 1,
															bgcolor: (theme) =>
																alpha(
																	theme
																		.palette
																		.info
																		.main,
																	0.08,
																),
															textAlign: "center",
														}}
													>
														<Speed
															sx={{
																color: "info.main",
																mb: 0.5,
															}}
														/>
														<Typography
															variant="h6"
															fontWeight={700}
														>
															{formatDuration(
																executionReport.avgTestDuration,
															)}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary"
														>
															Avg Test Duration
														</Typography>
													</Box>
												</Grid>
												<Grid item xs={4}>
													<Box
														sx={{
															p: 2,
															borderRadius: 1,
															bgcolor: (theme) =>
																alpha(
																	theme
																		.palette
																		.success
																		.main,
																	0.08,
																),
															textAlign: "center",
														}}
													>
														<CheckCircle
															sx={{
																color: "success.main",
																mb: 0.5,
															}}
														/>
														<Typography
															variant="h6"
															fontWeight={700}
														>
															{
																executionReport
																	.stepSummary
																	?.passedSteps
															}
															/
															{
																executionReport
																	.stepSummary
																	?.totalSteps
															}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary"
														>
															Steps Passed
															{executionReport
																.stepSummary
																?.skippedSteps >
																0 &&
																` (${executionReport.stepSummary.skippedSteps} skipped)`}
														</Typography>
													</Box>
												</Grid>
												<Grid item xs={4}>
													<Box
														sx={{
															p: 2,
															borderRadius: 1,
															bgcolor: (theme) =>
																alpha(
																	theme
																		.palette
																		.warning
																		.main,
																	0.08,
																),
															textAlign: "center",
														}}
													>
														<Schedule
															sx={{
																color: "warning.main",
																mb: 0.5,
															}}
														/>
														<Typography
															variant="h6"
															fontWeight={700}
														>
															{formatDuration(
																executionReport.totalDuration,
															)}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary"
														>
															Total Duration
														</Typography>
													</Box>
												</Grid>
											</Grid>

											{/* Slowest Tests */}
											{executionReport.slowestTests
												?.length > 0 && (
												<Box sx={{ mb: 2 }}>
													<Typography
														variant="subtitle2"
														fontWeight={600}
														sx={{ mb: 1 }}
													>
														Slowest Tests
													</Typography>
													{executionReport.slowestTests.map(
														(t, i) => (
															<Box
																key={i}
																sx={{
																	display:
																		"flex",
																	justifyContent:
																		"space-between",
																	alignItems:
																		"center",
																	py: 0.5,
																	px: 1,
																	borderRadius: 0.5,
																	bgcolor:
																		i %
																			2 ===
																		0
																			? (
																					theme,
																				) =>
																					alpha(
																						theme
																							.palette
																							.action
																							.hover,
																						0.04,
																					)
																			: "transparent",
																}}
															>
																<Typography
																	variant="body2"
																	noWrap
																	sx={{
																		maxWidth:
																			"60%",
																	}}
																>
																	{t.testName}
																</Typography>
																<Stack
																	direction="row"
																	spacing={1}
																	alignItems="center"
																>
																	<Chip
																		label={
																			t.status
																		}
																		size="small"
																		color={
																			getStatusChip(
																				t.status,
																			)
																				.color
																		}
																		sx={{
																			height: 20,
																			fontSize:
																				"0.7rem",
																		}}
																	/>
																	<Typography
																		variant="body2"
																		fontWeight={
																			600
																		}
																	>
																		{formatDuration(
																			t.duration,
																		)}
																	</Typography>
																</Stack>
															</Box>
														),
													)}
												</Box>
											)}
										</>
									)}
									{reportLoading && (
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												py: 2,
											}}
										>
											<CircularProgress size={20} />
										</Box>
									)}
								</CardContent>
							</Card>
						)}
					</Grid>
				</Grid>
			)}

			{/* Suite Report Dialog */}
			<Dialog
				open={suiteReportOpen}
				onClose={handleCloseSuiteReport}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<Stack direction="row" spacing={1} alignItems="center">
							<TrendingUp color="primary" />
							<Typography variant="h6" fontWeight={700}>
								{suiteReport?.suiteName || "Suite"} — Report
							</Typography>
						</Stack>
						<IconButton onClick={handleCloseSuiteReport}>
							<Close />
						</IconButton>
					</Box>
				</DialogTitle>
				<DialogContent>
					{suiteReportLoading ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								py: 4,
							}}
						>
							<CircularProgress />
						</Box>
					) : suiteReport ? (
						<Box>
							{/* Overview Stats */}
							<Grid container spacing={2} sx={{ mb: 3 }}>
								<Grid item xs={3}>
									<Box
										sx={{
											p: 2,
											borderRadius: 1,
											bgcolor: (theme) =>
												alpha(
													theme.palette.primary.main,
													0.08,
												),
											textAlign: "center",
										}}
									>
										<Typography
											variant="h5"
											fontWeight={700}
											color="primary"
										>
											{suiteReport.totalExecutions}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Total Runs
										</Typography>
									</Box>
								</Grid>
								<Grid item xs={3}>
									<Box
										sx={{
											p: 2,
											borderRadius: 1,
											bgcolor: (theme) =>
												alpha(
													theme.palette.success.main,
													0.08,
												),
											textAlign: "center",
										}}
									>
										<Typography
											variant="h5"
											fontWeight={700}
											color="success.main"
										>
											{suiteReport.overallPassRate}%
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Overall Pass Rate
										</Typography>
									</Box>
								</Grid>
								<Grid item xs={3}>
									<Box
										sx={{
											p: 2,
											borderRadius: 1,
											bgcolor: (theme) =>
												alpha(
													theme.palette.info.main,
													0.08,
												),
											textAlign: "center",
										}}
									>
										<Typography
											variant="h5"
											fontWeight={700}
											color="info.main"
										>
											{suiteReport.latestPassRate}%
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Latest Pass Rate
										</Typography>
									</Box>
								</Grid>
								<Grid item xs={3}>
									<Box
										sx={{
											p: 2,
											borderRadius: 1,
											bgcolor: (theme) =>
												alpha(
													theme.palette.warning.main,
													0.08,
												),
											textAlign: "center",
										}}
									>
										<Typography
											variant="h5"
											fontWeight={700}
											color="warning.main"
										>
											{formatDuration(
												suiteReport.avgDuration,
											)}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Avg Duration
										</Typography>
									</Box>
								</Grid>
							</Grid>

							{/* Execution History */}
							{suiteReport.executionHistory?.length > 0 && (
								<>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										sx={{ mb: 1 }}
									>
										Execution History
									</Typography>
									<TableContainer sx={{ mb: 3 }}>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Date</TableCell>
													<TableCell>
														Status
													</TableCell>
													<TableCell>
														Pass Rate
													</TableCell>
													<TableCell>Tests</TableCell>
													<TableCell>
														Duration
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{suiteReport.executionHistory.map(
													(entry) => {
														const chip =
															getStatusChip(
																entry.status,
															);
														return (
															<TableRow
																key={entry.id}
																hover
															>
																<TableCell>
																	<Typography variant="body2">
																		{entry.startedAt
																			? formatDateTimeShort(entry.startedAt)
																			: "-"}
																	</Typography>
																</TableCell>
																<TableCell>
																	<Chip
																		label={
																			entry.status
																		}
																		size="small"
																		color={
																			chip.color
																		}
																	/>
																</TableCell>
																<TableCell>
																	<Box
																		sx={{
																			display:
																				"flex",
																			alignItems:
																				"center",
																			gap: 1,
																		}}
																	>
																		<LinearProgress
																			variant="determinate"
																			value={
																				entry.passRate
																			}
																			color={
																				entry.passRate >=
																				80
																					? "success"
																					: entry.passRate >=
																						  50
																						? "warning"
																						: "error"
																			}
																			sx={{
																				height: 6,
																				borderRadius: 3,
																				flex: 1,
																				minWidth: 60,
																			}}
																		/>
																		<Typography
																			variant="caption"
																			fontWeight={
																				600
																			}
																		>
																			{
																				entry.passRate
																			}
																			%
																		</Typography>
																	</Box>
																</TableCell>
																<TableCell>
																	<Typography variant="body2">
																		{entry.passCount ||
																			0}
																		/
																		{entry.testCount ||
																			0}
																	</Typography>
																</TableCell>
																<TableCell>
																	{formatDuration(
																		entry.duration,
																	)}
																</TableCell>
															</TableRow>
														);
													},
												)}
											</TableBody>
										</Table>
									</TableContainer>
								</>
							)}

							{/* Test Stability */}
							{suiteReport.testStability?.length > 0 && (
								<>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										sx={{ mb: 1 }}
									>
										Test Stability
									</Typography>
									{suiteReport.testStability.map((t, i) => (
										<Box
											key={i}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 2,
												py: 0.75,
												px: 1,
												borderRadius: 0.5,
												bgcolor:
													i % 2 === 0
														? (theme) =>
																alpha(
																	theme
																		.palette
																		.action
																		.hover,
																	0.04,
																)
														: "transparent",
											}}
										>
											<Typography
												variant="body2"
												noWrap
												sx={{
													flex: 1,
													minWidth: 0,
												}}
											>
												{t.testName}
											</Typography>
											<LinearProgress
												variant="determinate"
												value={t.passRate}
												color={
													t.passRate >= 80
														? "success"
														: t.passRate >= 50
															? "warning"
															: "error"
												}
												sx={{
													height: 6,
													borderRadius: 3,
													width: 100,
												}}
											/>
											<Typography
												variant="caption"
												fontWeight={600}
												sx={{ minWidth: 45 }}
											>
												{t.passRate}% ({t.passCount}/
												{t.totalCount})
											</Typography>
										</Box>
									))}
								</>
							)}
						</Box>
					) : null}
				</DialogContent>
			</Dialog>
		</Box>
	);
}
